import { useCallback, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";

import { TextPrimary, TextSecondary, AuditsContainer } from "theme";
import { useAPY, useTVL } from "dapp-sdk";

// assets
import { VerifiedUser } from "icons";

// components
import Card from "@components/Card";
import AuditsModal from "@components/AuditsModal";
import Icon from "@components/Icon";

import { StrapiCenterHeaderData } from "@customtypes/global";
import { formatNumber } from "@utils/globalCommon";

import usePageData from "@hooks/usePageData";

import styles from "./styles.module.scss";

interface CardHeader {
  activeTab: string;
  title: string;
  currentPrice?: number;
  subTitle?: string;
}

const CenterHeader = ({ activeTab, subTitle, title }: CardHeader) => {
  const [isAuditsModalOpen, setIsAuditsModalOpen] = useState(false);
  const tvlData = useTVL({});
  const apyData = useAPY({});
  const apy = apyData?.apy ? Number(apyData.apy.toFixed(2)) : 0;

  const values = usePageData("$.page.data.center_header[0].values")[0];
  const audits = usePageData("$.page.data.audits")[0];
  const buhBounty = usePageData("$.page.data.bug_bounty")[0];

  const getAPYSection = useCallback(
    (section: StrapiCenterHeaderData) => {
      return (
        <Flex justifyContent="space-between">
          <TextSecondary>{section.title}</TextSecondary>
          <TextPrimary
            fontWeight="600"
            lineHeight="24px"
            fontSize={{ base: "12px", lg: "16px" }}
          >
            {apy}%
          </TextPrimary>
        </Flex>
      );
    },
    [apy]
  );

  const getTotalStakedSection = useCallback(
    (section: StrapiCenterHeaderData) => {
      return (
        <Flex justifyContent="space-between">
          <Text color="textSecondary">{section.title}</Text>
          <Flex alignItems="center">
            {tvlData?.tvl ? (
              <>
                <TextPrimary
                  fontWeight="600"
                  lineHeight="24px"
                  fontSize={{ base: "12px", lg: "16px" }}
                >
                  {`${formatNumber(tvlData.tvl.value)} ${
                    process.env.NEXT_PUBLIC_TOKEN
                  }`}
                </TextPrimary>
                <TextSecondary ml="6px">|</TextSecondary>
                <TextSecondary
                  ml="6px"
                  fontWeight={{ base: "400", lg: "600" }}
                  lineHeight="20px"
                  fontSize={{ base: "10px", lg: "14px" }}
                >
                  ${formatNumber(tvlData.tvl.usdTvl)}
                </TextSecondary>
              </>
            ) : (
              "-"
            )}
          </Flex>
        </Flex>
      );
    },
    [tvlData.tvl]
  );

  const getCenterHeaderItem = (section: StrapiCenterHeaderData) => {
    switch (section.key) {
      case "apy":
        return getAPYSection(section);
      case "totalStakedValue":
        return getTotalStakedSection(section);
    }
  };

  return (
    <Box width="100%">
      <Flex
        justifyContent="space-between"
        alignItems={{ base: "flex-start", xl: "center" }}
        direction="column"
      >
        <Flex
          alignItems="flex-start"
          direction="column"
          justifyContent="center"
          textAlign="center"
          width="100%"
        >
          <Flex alignItems="center">
            <TextPrimary
              fontWeight="600"
              fontSize={{ base: "24px", md: "32px" }}
              lineHeight={{ base: "28px", md: "38px" }}
            >
              {title ? title : `${activeTab} ${process.env.NEXT_PUBLIC_TOKEN}`}
            </TextPrimary>
            {audits?.title ? (
              <AuditsContainer
                onClick={() => setIsAuditsModalOpen(true)}
                ml="4px"
              >
                <Icon
                  height="16px"
                  width="16px"
                  Icon={VerifiedUser}
                  color="#38A169"
                />
                <TextPrimary fontWeight="400" fontSize="14px" lineHeight="20px">
                  {audits?.title}
                </TextPrimary>
              </AuditsContainer>
            ) : null}
          </Flex>
          {subTitle && (
            <TextSecondary
              mt="4px"
              fontWeight="400"
              fontSize={{ base: "12px", md: "14px" }}
              lineHeight={{ base: "18px", md: "20px" }}
              textAlign="left"
            >
              {subTitle}
            </TextSecondary>
          )}
        </Flex>

        {values?.length ? (
          <Card
            p={{ base: "1.5rem 1rem", md: "1.5rem 2rem" }}
            bg="backgroundSecondary"
            w="100%"
            mt={{ base: "1rem", md: "2rem" }}
            fontSize={{ base: "12px", md: "14px" }}
            fontWeight={{ base: "400", md: "600" }}
            borderRadius="1rem"
            className={styles.centerHeader}
          >
            {values.map((data: StrapiCenterHeaderData) => (
              <div key={data.key} className={styles.centerHeaderItem}>
                {getCenterHeaderItem(data)}
              </div>
            ))}
          </Card>
        ) : null}
      </Flex>
      <AuditsModal
        isOpen={isAuditsModalOpen}
        closeModal={() => setIsAuditsModalOpen(false)}
        title={audits?.title}
        subTitle={audits?.sub_title}
        auditsData={audits?.audits_data}
        bugBountySection={buhBounty}
      />
    </Box>
  );
};

export default CenterHeader;
